<script setup>
import { ref, reactive, watch } from 'vue';
import Dialog from 'primevue/dialog';
import Button from 'primevue/button';
import InputText from 'primevue/inputtext';
import Dropdown from 'primevue/dropdown';
import axios from 'axios';
import { useToast } from 'primevue/usetoast';

const props = defineProps({
  modelValue: { type: Boolean, required: true }
});
const emit = defineEmits(['update:modelValue', 'created']);

const toast = useToast();

const visible = ref(props.modelValue);

watch(() => props.modelValue, (v) => { visible.value = v; });
watch(visible, (v) => { emit('update:modelValue', v); });

const loading = ref(false);
const form = reactive({
  first_name: '',
  last_name: '',
  email: '',
  phone: '',
  gender: ''
});

const genderOptions = [
  { label: 'Weiblich', value: 'female' },
  { label: 'Männlich', value: 'male' },
  { label: 'Divers', value: 'non_binary' }
];

const resetForm = () => {
  form.first_name = '';
  form.last_name = '';
  form.email = '';
  form.phone = '';
  form.gender = '';
};

const extractError = (e, defaultMsg = 'Vorgang fehlgeschlagen') => {
    const data = e.response?.data;
    if (data?.errors) {
        const first = Object.values(data.errors).flat()[0];
        if (first) return first;
    }
    return data?.message || e.message || defaultMsg;
};

const submit = async () => {
  if (!form.first_name?.trim() || !form.last_name?.trim() || !form.email?.trim() || !form.gender) {
    toast.add({ severity: 'warn', summary: 'Fehlende Daten', detail: 'Vorname, Nachname, E-Mail und Geschlecht sind erforderlich.', life: 3000 });
    return;
  }
  loading.value = true;
  try {
    const { data } = await axios.post('/ajax/customers/quick-add', form);
    toast.add({ severity: 'success', summary: 'Erfolg', detail: 'Laufkunde erfolgreich angelegt.', life: 3000 });
    emit('created', data.customer);
    visible.value = false;
    resetForm();
  } catch (e) {
    console.error('Quick add failed', e);
    toast.add({ severity: 'error', summary: 'Fehler', detail: extractError(e, 'Kunde konnte nicht angelegt werden.'), life: 5000 });
  } finally {
    loading.value = false;
  }
};
</script>

<template>
  <Dialog 
    v-model:visible="visible" 
    :modal="true" 
    :style="{ width: '450px' }" 
    header="Laufkunde schnell anlegen"
  >
    <div class="space-y-4 pt-4">
      <div class="grid grid-cols-2 gap-4">
        <div class="space-y-1">
          <label class="text-sm font-bold text-gray-600">Vorname *</label>
          <InputText v-model="form.first_name" class="w-full" placeholder="Max" />
        </div>
        <div class="space-y-1">
          <label class="text-sm font-bold text-gray-600">Nachname *</label>
          <InputText v-model="form.last_name" class="w-full" placeholder="Mustermann" />
        </div>
      </div>
      <div class="space-y-1">
        <label class="text-sm font-bold text-gray-600">E-Mail *</label>
        <InputText v-model="form.email" type="email" class="w-full" placeholder="max@example.com" />
      </div>
      <div class="space-y-1">
        <label class="text-sm font-bold text-gray-600">Telefon (Optional)</label>
        <InputText v-model="form.phone" class="w-full" placeholder="+41 79 123 45 67" />
      </div>
      <div class="space-y-1">
        <label class="text-sm font-bold text-gray-600">Geschlecht *</label>
        <Dropdown v-model="form.gender" :options="genderOptions" optionLabel="label" optionValue="value" placeholder="Bitte wählen" class="w-full" />
      </div>
    </div>
    <template #footer>
      <div class="flex justify-end gap-3 mt-4">
        <Button label="Abbrechen" @click="visible = false" class="!bg-gray-100 !text-gray-600 !border-transparent hover:!bg-gray-200" />
        <Button label="Kunde anlegen" @click="submit" :loading="loading" class="!bg-black !text-[#FFF053] !border-black hover:!bg-gray-900" />
      </div>
    </template>
  </Dialog>
</template>
